import { useState, useCallback, useEffect } from "react";

interface UseUploaderOptions {
  onFileSelect: (file: File | File[] | null) => void;
  multiple?: boolean;
}

/**
 * Hook for handling file upload functionality.
 * Manages object URLs for file previews and cleanup.
 */
export const useUploader = ({
  onFileSelect,
  multiple = false,
}: UseUploaderOptions) => {
  const [objectUrls, setObjectUrls] = useState<string[]>([]);

  const getPreviewUrl = useCallback(
    (file: File, index: number) => {
      if (!file) return null;

      const existingUrl = objectUrls[index];
      if (existingUrl) return existingUrl;

      try {
        const newUrl = URL.createObjectURL(file);
        setObjectUrls((prev) => {
          const next = [...prev];
          next[index] = newUrl;
          return next;
        });
        return newUrl;
      } catch (error) {
        console.error("Failed to create preview URL:", error);
        return null;
      }
    },
    [objectUrls],
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      // Cleanup old object URLs
      objectUrls.forEach(URL.revokeObjectURL);
      setObjectUrls([]);

      if (!files?.length) {
        onFileSelect(null);
        return;
      }

      onFileSelect(multiple ? Array.from(files) : files[0]);
    },
    [multiple, objectUrls, onFileSelect],
  );

  const handleDelete = useCallback(
    (index?: number) => {
      if (typeof index === "number" && objectUrls[index]) {
        URL.revokeObjectURL(objectUrls[index]);
        setObjectUrls((prev) => prev.filter((_, i) => i !== index));
      } else {
        objectUrls.forEach(URL.revokeObjectURL);
        setObjectUrls([]);
      }
      onFileSelect(null);
    },
    [objectUrls, onFileSelect],
  );

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrls.forEach(URL.revokeObjectURL);
    };
  }, [objectUrls]);

  return {
    handleFileSelect,
    handleDelete,
    getPreviewUrl,
  };
};
